import { Formik } from 'formik'
import filter from 'lodash-es/filter'
import isEqual from 'lodash-es/isEqual'
import { reverse } from 'named-urls'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Dashboard, PageLayout } from '@components/common'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  BackButton,
  ConfirmModal,
  HeadContent,
  Loading,
  SaveShortcutListener,
  SortableBlocks,
  useModalContext,
} from '@fourthwall/components'
import { arrayMove } from '@fourthwall/utils/dist/array'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { AddButtons } from './components/AddButtons'
import { ProductSection } from './components/ProductSection'
import { validationSchema } from './consts'
import type { FormValues, Payload, ProductSectionsViewProps } from './types'

export const ProductSectionsView = ({
  sections,
  productName = '',
  productId,
  isDigital,
  productIsFetching,
  onSubmit,
}: ProductSectionsViewProps) => {
  const { open } = useModalContext()
  const location = useLocation()

  const getInitialValues: () => FormValues = () => ({
    additionalSections: sections.map((section) => ({ ...section, bodyHtml: section.bodyHtml })),
  })

  const [initialValues, setInitialValues] = useState(getInitialValues())

  useEffect(() => {
    setInitialValues(getInitialValues())
  }, [sections])

  const createPayloadFromValues: (values: FormValues) => Payload = (values) => ({
    additionalSections: values.additionalSections.map((section) => ({
      ...section,
      bodyHtml: section.bodyHtml || '',
    })),
  })

  const handleSortChange = (
    values: FormValues,
    setValues: (values: FormValues) => void,
    oldVariantIndex: number,
    newVariantIndex: number,
  ) => {
    const sortedArray = arrayMove([...values.additionalSections], oldVariantIndex, newVariantIndex)

    setValues({ additionalSections: sortedArray })
  }

  const remove = (values: FormValues, setValues: (values: FormValues) => void, index: number) => {
    const filteredSections = filter(
      [...values.additionalSections],
      (section, sectionIndex) => sectionIndex !== index,
    )

    setValues({ additionalSections: filteredSections })
  }

  const handleRemove = (
    values: FormValues,
    setValues: (values: FormValues) => void,
    index: number,
  ) => {
    const section = values.additionalSections[index]

    if (section) {
      open(ConfirmModal, {
        title: `Are you sure you want to delete ${section.title} section?`,
        confirmLabel: 'Yes, delete',
        cancelLabel: 'No, cancel',
        onConfirm: () => remove(values, setValues, index),
      })
    }
  }

  const handleSubmit = (values: FormValues) => {
    onSubmit(createPayloadFromValues(values))
  }

  return (
    <>
      <HeadContent title={`Sections \u2013 ${productName}`} />
      <Loading isLoading={productIsFetching}>
        <PageLayout size="large">
          <Dashboard.Section
            name="Edit additional sections"
            title="These sections, often appearing below the description, add additional information to help your fans make their purchase."
            topContent={
              <BackButton
                label={productName}
                to={{
                  pathname: reverse(routing.products.all.product.self, { productId }),
                  search: location.search,
                }}
              />
            }
            bottomMargin="large"
          >
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={handleSubmit}
            >
              {({ values, submitForm, isValid, resetForm, setValues, isSubmitting }) => {
                const isFormDirty = !isEqual(
                  createPayloadFromValues(initialValues),
                  createPayloadFromValues(values),
                )

                return (
                  <>
                    <div style={{ maxWidth: 584 }}>
                      <SortableBlocks
                        onSortChange={(oldIndex, newIndex) =>
                          handleSortChange(values, setValues, oldIndex, newIndex)
                        }
                        onRemove={(index) => handleRemove(values, setValues, index)}
                      >
                        {values.additionalSections.map(({ type }, index) => (
                          <SortableBlocks.Item key={type}>
                            <ProductSection key={index} index={index} />
                          </SortableBlocks.Item>
                        ))}
                      </SortableBlocks>
                    </div>
                    <AddButtons
                      allValues={values}
                      setValues={setValues}
                      isDigitalProduct={isDigital}
                    />
                    <SaveShortcutListener disabled={!isFormDirty} onSave={submitForm} />
                    <ConfirmBar
                      isOpen={isFormDirty}
                      isLoading={isSubmitting}
                      onConfirm={submitForm}
                      onCancel={() => resetForm({ values: initialValues })}
                    />
                    <LeavingGuard when={isFormDirty} isValid={isValid} onSave={submitForm} />
                  </>
                )
              }}
            </Formik>
          </Dashboard.Section>
        </PageLayout>
      </Loading>
    </>
  )
}
